import {
  Button,
  ConfirmDialog,
  DataTable,
  ErrorState,
  Label,
  RowActions,
  StatusBadge,
  Switch,
  TOUCH,
  ToggleGroup,
  ToggleGroupItem,
} from "@nymbl/nos2-rules";
import { CalendarOff } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { AskCancelDialog } from "@/components/pto/AskCancelDialog";
import { STATUS_TONES } from "@/components/pto/pto-colors";
import { OFF_KIND_TONES } from "@/components/team/team-colors";
import { useDeleteRequest } from "@/hooks/use-pto";
import { PTO_REQUEST_VIEW_LABELS, PTO_REQUEST_VIEWS, type PtoListView } from "@/lib/enums";
import { formatDayCount, formatPartialHours, typeLabel } from "@/lib/pto/days";
import {
  defaultRequestOrder,
  filterMyRequests,
  MY_REQUEST_COLUMNS,
  type MyRequest,
  type RequestAction,
  requestActions,
  STATUS_LABELS,
  shownDays,
} from "@/lib/pto/my-requests";
import { withCells } from "@/lib/table-columns";
import { formatDateRange } from "@/lib/team/pto-list";
import { offKind } from "@/lib/team/time-off";
import { cn } from "@/lib/utils";
import type { MyRequestsView } from "@/schemas/pto";

const NO_ROWS: MyRequest[] = [];

const ACTION_LABELS: Record<RequestAction, string> = {
  edit: "Edit",
  cancelRequest: "Cancel request",
  delete: "Delete",
  cancelTimeOff: "Cancel time off",
  askCancel: "Ask to cancel",
};

function datesText(r: MyRequest): string {
  const range = formatDateRange(r.startDate, r.endDate);
  const partial = formatPartialHours(r.hoursPerDay);
  return partial ? `${range} · ${partial}` : range;
}

interface MyRequestsTableProps {
  view: MyRequestsView | undefined;
  isError: boolean;
  onRetry: () => void;
  onEdit: (r: MyRequest) => void;
  onRequest: () => void;
}

/**
 * My requests (spec 1.3, 1.5) on the table kit: the search, then the Upcoming | Past and Show
 * holidays toggles; every column sorts and filters from its header. Before the first day an
 * approved request is cancelled directly; after it, the approver is asked (D2).
 */
const COLUMNS = withCells(MY_REQUEST_COLUMNS, {
  dates: { render: datesText },
  type: {
    render: (r) => {
      const kind = offKind(r.typeEnum);
      return (
        <StatusBadge
          status={kind}
          tones={OFF_KIND_TONES}
          labels={{ [kind]: typeLabel(r.typeEnum, r.typeName) }}
        />
      );
    },
  },
  days: { render: (r) => formatDayCount(shownDays(r)) },
  status: {
    render: (r) => <StatusBadge status={r.status} tones={STATUS_TONES} labels={STATUS_LABELS} />,
  },
  note: {
    className: "max-w-72",
    render: (r) => (
      <>
        {r.description && <div className="line-clamp-2">{r.description}</div>}
        {r.status === "rejected" && r.rejectionNote && (
          <div className="text-sm text-danger">{r.rejectionNote}</div>
        )}
      </>
    ),
  },
  approver: { render: (r) => r.approverName ?? "—" },
});

/**
 * My requests (spec 1.3, 1.5): Upcoming | Past and Show holidays are the table's own controls.
 * Before the first day an approved request is cancelled directly; after it, the approver is asked
 * (D2).
 */
export function MyRequestsTable({
  view,
  isError,
  onRetry,
  onEdit,
  onRequest,
}: MyRequestsTableProps) {
  const holidaysId = useId();
  const [tab, setTab] = useState<PtoListView>("upcoming");
  const [showHolidays, setShowHolidays] = useState(false);
  const [confirming, setConfirming] = useState<{ action: RequestAction; r: MyRequest } | null>(
    null,
  );
  const [asking, setAsking] = useState<MyRequest | null>(null);
  const del = useDeleteRequest();

  // Unsorted, the rows keep the view's own order (rejected first, then by start).
  const rows = useMemo(
    () =>
      view
        ? [...filterMyRequests(view.rows, tab, view.today, showHolidays)].sort(
            defaultRequestOrder(tab),
          )
        : NO_ROWS,
    [view, tab, showHolidays],
  );

  function onAction(a: RequestAction, r: MyRequest) {
    if (a === "edit") onEdit(r);
    else if (a === "askCancel") setAsking(r);
    else setConfirming({ action: a, r });
  }

  return (
    <>
      <DataTable
        label={`${PTO_REQUEST_VIEW_LABELS[tab]} requests`}
        title="My requests"
        search={{ placeholder: "Search requests…" }}
        exportName="pto-requests"
        columns={COLUMNS}
        rows={rows}
        rowKey={(r) => r.id}
        loading={!view && !isError}
        error={isError && !view ? new Error("Requests didn't load") : undefined}
        onRetry={onRetry}
        notice={
          isError && view ? (
            <ErrorState message="Requests didn't load" onRetry={onRetry} />
          ) : undefined
        }
        empty={{
          title: view?.rows.length === 0 ? `No time off in ${view.year}` : "Nothing here",
          icon: CalendarOff,
          action:
            tab === "upcoming" ? (
              <Button className={TOUCH} onClick={onRequest}>
                Request time off
              </Button>
            ) : undefined,
        }}
        controls={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
            <ToggleGroup
              type="single"
              variant="outline"
              aria-label="View"
              value={tab}
              onValueChange={(v) => {
                const next = PTO_REQUEST_VIEWS.find((x) => x === v);
                if (next) setTab(next);
              }}
            >
              {PTO_REQUEST_VIEWS.map((v) => (
                <ToggleGroupItem key={v} value={v} className={cn(TOUCH, "px-3")}>
                  {PTO_REQUEST_VIEW_LABELS[v]}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <div className="flex min-h-11 items-center gap-2">
              <Switch id={holidaysId} checked={showHolidays} onCheckedChange={setShowHolidays} />
              <Label htmlFor={holidaysId} className="min-h-11 cursor-pointer whitespace-nowrap">
                Show holidays
              </Label>
            </div>
          </div>
        }
        rowActions={(r) => {
          // HR-loaded company holidays, locked open requests and closed months get no actions.
          const actions = view ? requestActions(r, view.today) : [];
          if (actions.length === 0) return null;
          return (
            <RowActions
              label={`Actions for ${datesText(r)}`}
              actions={actions.map((a) => ({
                label: ACTION_LABELS[a],
                destructive: a !== "edit",
                onSelect: () => onAction(a, r),
              }))}
            />
          );
        }}
      />

      <ConfirmDialog
        open={confirming !== null}
        onOpenChange={(open) => !open && !del.isPending && setConfirming(null)}
        title={confirming ? `${ACTION_LABELS[confirming.action]}?` : ""}
        description={confirming ? datesText(confirming.r) : ""}
        confirmLabel={confirming ? ACTION_LABELS[confirming.action] : "Confirm"}
        cancelLabel="Keep"
        variant="destructive"
        loading={del.isPending}
        onConfirm={() => {
          // A failure keeps the dialog open; a retry after a 504 settles from the real state.
          if (confirming)
            del.mutate(
              { id: confirming.r.id, status: confirming.r.status },
              { onSuccess: () => setConfirming(null) },
            );
        }}
      />
      <AskCancelDialog
        key={asking?.id ?? "none"}
        request={asking}
        onClose={() => setAsking(null)}
      />
    </>
  );
}
